/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.75: Admin ops console. Multiple "fetch on dependency change"
 * useEffects with setState in async IIFEs. eslint-plugin-react-hooks v5's
 * set-state-in-effect rule is over-eager for this canonical pattern.
 * Other React-19 lints (rules-of-hooks, exhaustive-deps) remain enabled.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatDT } from "@/lib/format";
import {
  ShieldCheck, Activity, Mail, Flag, HardDrive, Banknote, Heart,
  History, Network, RefreshCw, ChevronDown, ChevronUp, AlertTriangle,
  ShieldAlert, Megaphone, Filter, Plus, Trash2, Code2, LayoutDashboard,
  UserPlus, Sparkles,
} from "lucide-react";

/**
 * Unified admin operations console — Phases 2-4.
 *
 * One collapsible section per concern, all backed by GET endpoints
 * we added in `routes/admin_ops.py` (and `routes/admin_impersonation.py`
 * for the audit log). Single component on purpose: it lets us share
 * loading states, keep the visual rhythm consistent, and avoid n+1
 * route imports in the admin page.
 */
export default function AdminOpsConsole() {
  const [open, setOpen] = useState({
    health: false,    // Phase 2 #4
    audit: false,     // Phase 1 #10 (already shipped, surfaced here)
    flags: false,     // Phase 4 #7
    broadcast: false, // v77.99.16 — admin broadcast message
    storage: false,   // Phase 4 #8
    revenue: false,   // Phase 4 #11
    health_hh: false, // Phase 4 #12
    email: false,     // Phase 3 #6
    abuse: false,     // Phase 5 — trial abuse (v77.99.3)
    patterns: false,  // v77.99.16 — admin-editable test email patterns
    refactor: false,  // v77.99.19 — backend refactor scoreboard
    frontend: false,  // v77.99.20 — frontend health scoreboard
    pending: false,   // v77.99.20 — promote pending invites
    releases: false,  // v77.99.21 — admin release notes
  });
  return (
    <section
      className="space-y-3"
      data-testid="admin-ops-console"
    >
      <h2 className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
        Operations console
      </h2>
      <div className="rounded-md border border-[#D9E1E3] bg-white divide-y divide-[#E2E8EC]">
        <Panel
          icon={Banknote} label="Revenue & subscriptions" tone="green"
          open={open.revenue} onToggle={() => setOpen((o) => ({ ...o, revenue: !o.revenue }))}
          testid="ops-panel-revenue"
        >
          <RevenuePanel active={open.revenue} />
        </Panel>
        <Panel
          icon={Activity} label="System health (last 7d)" tone="amber"
          open={open.health} onToggle={() => setOpen((o) => ({ ...o, health: !o.health }))}
          testid="ops-panel-health"
        >
          <SystemHealthPanel active={open.health} />
        </Panel>
        <Panel
          icon={History} label="Admin audit log" tone="violet"
          open={open.audit} onToggle={() => setOpen((o) => ({ ...o, audit: !o.audit }))}
          testid="ops-panel-audit"
        >
          <AuditLogPanel active={open.audit} />
        </Panel>
        <Panel
          icon={Flag} label="Feature flags" tone="violet"
          open={open.flags} onToggle={() => setOpen((o) => ({ ...o, flags: !o.flags }))}
          testid="ops-panel-flags"
        >
          <FlagsPanel active={open.flags} />
        </Panel>
        <Panel
          icon={Megaphone} label="Admin broadcast message" tone="amber"
          open={open.broadcast} onToggle={() => setOpen((o) => ({ ...o, broadcast: !o.broadcast }))}
          testid="ops-panel-broadcast"
        >
          <BroadcastPanel active={open.broadcast} />
        </Panel>
        <Panel
          icon={Filter} label="Test email patterns" tone="grey"
          open={open.patterns} onToggle={() => setOpen((o) => ({ ...o, patterns: !o.patterns }))}
          testid="ops-panel-test-patterns"
        >
          <TestPatternsPanel active={open.patterns} />
        </Panel>
        <Panel
          icon={HardDrive} label="Storage usage" tone="grey"
          open={open.storage} onToggle={() => setOpen((o) => ({ ...o, storage: !o.storage }))}
          testid="ops-panel-storage"
        >
          <StoragePanel active={open.storage} />
        </Panel>
        <Panel
          icon={Heart} label="Household health rollup" tone="grey"
          open={open.health_hh} onToggle={() => setOpen((o) => ({ ...o, health_hh: !o.health_hh }))}
          testid="ops-panel-household-health"
        >
          <HouseholdHealthPanel active={open.health_hh} />
        </Panel>
        <Panel
          icon={Mail} label="Email delivery log" tone="grey"
          open={open.email} onToggle={() => setOpen((o) => ({ ...o, email: !o.email }))}
          testid="ops-panel-email"
        >
          <EmailLogPanel active={open.email} />
        </Panel>
        <Panel
          icon={ShieldAlert} label="Trial abuse (last 30d)" tone="red"
          open={open.abuse} onToggle={() => setOpen((o) => ({ ...o, abuse: !o.abuse }))}
          testid="ops-panel-trial-abuse"
        >
          <TrialAbusePanel active={open.abuse} />
        </Panel>
        <Panel
          icon={Code2} label="Refactor scoreboard" tone="violet"
          open={open.refactor} onToggle={() => setOpen((o) => ({ ...o, refactor: !o.refactor }))}
          testid="ops-panel-refactor"
        >
          <RefactorScoreboardPanel active={open.refactor} />
        </Panel>
        <Panel
          icon={LayoutDashboard} label="Frontend health scoreboard" tone="violet"
          open={open.frontend} onToggle={() => setOpen((o) => ({ ...o, frontend: !o.frontend }))}
          testid="ops-panel-frontend"
        >
          <FrontendScoreboardPanel active={open.frontend} />
        </Panel>
        <Panel
          icon={UserPlus} label="Promote pending invites" tone="violet"
          open={open.pending} onToggle={() => setOpen((o) => ({ ...o, pending: !o.pending }))}
          testid="ops-panel-pending-invites"
        >
          <PendingInvitesPanel active={open.pending} />
        </Panel>
        <Panel
          icon={Sparkles} label="Release notes (What's new)" tone="violet"
          open={open.releases} onToggle={() => setOpen((o) => ({ ...o, releases: !o.releases }))}
          testid="ops-panel-releases"
        >
          <ReleaseNotesPanel active={open.releases} />
        </Panel>
      </div>
    </section>
  );
}

const _TONE_BG = {
  green: "bg-[#E5F1EA] text-[#1E5A1E]",
  amber: "bg-[#FDF4E2] text-[#8F6B1F]",
  red:   "bg-[#FBEFED] text-[#8C2A22]",
  violet:"bg-[#ECE3F3] text-[#4E3877]",
  grey:  "bg-[#F0EFEE] text-[#6F828A]",
};

function Panel({ icon: Icon, label, tone = "grey", open, onToggle, testid, children }) {
  return (
    <div data-testid={testid}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 hover:bg-[#F8FAFB] transition-colors text-left"
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
      >
        <span className="flex items-center gap-3">
          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-sm ${_TONE_BG[tone] || _TONE_BG.grey}`}>
            <Icon className="w-4 h-4" />
          </span>
          <span className="text-[14px] font-medium text-[#2E3A3F]">{label}</span>
        </span>
        {open
          ? <ChevronUp className="w-4 h-4 text-[#6F828A]" />
          : <ChevronDown className="w-4 h-4 text-[#6F828A]" />}
      </button>
      {open && (
        <div className="px-4 pb-4 sm:px-5 sm:pb-5">
          {children}
        </div>
      )}
    </div>
  );
}

function useAdminFetch(url, active) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const reload = async () => {
    setBusy(true); setErr(null);
    try { const { data } = await api.get(url); setData(data); }
    catch (e) { setErr(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (active && !data && !busy) reload(); }, [active, url]);
  return { data, busy, err, reload };
}

function ReloadButton({ onClick, busy }) {
  return (
    <Button type="button" size="sm" variant="outline" onClick={onClick} disabled={busy} className="rounded-sm h-8 text-[12px]">
      <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> Reload
    </Button>
  );
}

// v77.99.75 — Hoisted out of RevenuePanel render. Defining a
// component inside another component on every render destroys the
// subtree's DOM each frame; lift the definition to module scope.
function StatTile({ label, value, hint }) {
  return (
    <div className="rounded-sm border border-[#E2E8EC] bg-[#F8FAFB] p-3">
      <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">{label}</div>
      <div className="text-[20px] font-semibold tabular-nums mt-1 font-mono-data text-[#2E3A3F]">{value}</div>
      {hint && <div className="text-[11px] text-[#6F828A] mt-0.5">{hint}</div>}
    </div>
  );
}

// ---------- Revenue ----------
function RevenuePanel({ active }) {
  const { data, busy, err, reload } = useAdminFetch("/admin/revenue-metrics", active);
  if (err) return <div className="text-sm text-[#8C2A22]">Couldn&apos;t load: {err}</div>;
  if (!data) return <div className="text-xs text-[#6F828A]">Loading…</div>;
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><ReloadButton onClick={reload} busy={busy} /></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="ops-revenue-stats">
        <StatTile label="MRR (combined)" value={`€${(data.mrr_eur || 0).toFixed(2)}`} hint={`Monthly + Yearly/12`} />
        <StatTile label="MRR · Monthly" value={`€${(data.mrr_monthly_eur || 0).toFixed(2)}`} hint={`${data.active_monthly || 0} active · €${data.monthly_price_eur}/mo`} />
        <StatTile label="MRR · Yearly" value={`€${(data.mrr_yearly_eur || 0).toFixed(2)}`} hint={`${data.active_yearly || 0} active · €${data.yearly_price_eur}/yr`} />
        <StatTile label="ARR" value={`€${(data.arr_eur || 0).toFixed(2)}`} />
        <StatTile label="Active paid" value={data.active_paid} hint={`${data.active_monthly || 0} mo + ${data.active_yearly || 0} yr`} />
        <StatTile label="Active trials" value={data.active_trials} />
        <StatTile label="Trials expiring 48h" value={data.trials_expiring_soon} hint="Action needed" />
        <StatTile label="Past due" value={data.past_due} />
        <StatTile label="Cancelled" value={data.cancelled} />
        <StatTile label="Manual premium" value={data.manual_premium} hint="Admin-granted" />
        <StatTile label="Trial → paid" value={`${data.trial_to_paid_conversion_pct}%`} hint={`${data.paid_ever}/${data.trials_ever}`} />
      </div>
    </div>
  );
}

// ---------- System health ----------
function SystemHealthPanel({ active }) {
  const { data, busy, err, reload } = useAdminFetch("/admin/system-health?limit=20", active);
  if (err) return <div className="text-sm text-[#8C2A22]">Couldn&apos;t load: {err}</div>;
  if (!data) return <div className="text-xs text-[#6F828A]">Loading…</div>;
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><ReloadButton onClick={reload} busy={busy} /></div>
      {data.items.length === 0 ? (
        <div className="text-sm text-[#1E5A1E] bg-[#E5F1EA] rounded-sm p-3 inline-flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> No operational errors in the last 7 days.
        </div>
      ) : (
        <ul className="divide-y divide-[#E2E8EC]" data-testid="ops-health-list">
          {data.items.map((it, i) => (
            <li key={`${it.ts || "x"}-${it.channel || "x"}-${i}`} className="py-2.5 flex items-start gap-3">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-sm bg-[#FBEFED] text-[#8C2A22] shrink-0">
                <AlertTriangle className="w-3.5 h-3.5" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] text-[#2E3A3F] flex flex-wrap items-baseline gap-2">
                  <span className="font-semibold capitalize">{it.channel}</span>
                  <span className="text-[#6F828A]">{it.type}</span>
                  <span className="text-[10px] text-[#6F828A] font-mono-data ml-auto">{formatDT(it.ts)}</span>
                </div>
                {it.message && <div className="text-[12px] text-[#6F828A] break-words mt-0.5">{it.message}</div>}
                {(it.user_id || it.to || it.subject) && (
                  <div className="text-[11px] text-[#6F828A] mt-0.5 font-mono-data break-all">
                    {it.to && <>to {it.to} · </>}
                    {it.subject && <>&quot;{it.subject}&quot; · </>}
                    {it.user_id && <>user {it.user_id.slice(0, 8)}</>}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Audit log ----------
function AuditLogPanel({ active }) {
  const { data, busy, err, reload } = useAdminFetch("/admin/audit-log?limit=30", active);
  if (err) return <div className="text-sm text-[#8C2A22]">Couldn&apos;t load: {err}</div>;
  if (!data) return <div className="text-xs text-[#6F828A]">Loading…</div>;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <a
          href="/app/admin/audit-log"
          className="text-[12px] text-[#1F9FA4] hover:underline font-medium"
          data-testid="ops-audit-view-full"
        >
          View full audit log →
        </a>
        <ReloadButton onClick={reload} busy={busy} />
      </div>
      {data.entries.length === 0 ? (
        <div className="text-sm text-[#6F828A]">No audit entries yet.</div>
      ) : (
        <ul className="divide-y divide-[#E2E8EC]" data-testid="ops-audit-list">
          {data.entries.map((e) => (
            <li key={e.id} className="py-2.5">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-[12.5px] font-semibold text-[#2E3A3F]">{e.action}</span>
                <span className="text-[12px] text-[#6F828A]">by {e.admin_email}</span>
                {e.target_label && (
                  <span className="text-[12px] text-[#6F828A]">→ {e.target_label}</span>
                )}
                <span className="text-[10px] font-mono-data text-[#6F828A] ml-auto">{formatDT(e.ts)}</span>
              </div>
              {e.result && e.result !== "success" && (
                <div className="text-[11px] text-[#8C2A22] mt-0.5">{e.result}</div>
              )}
              {e.meta && Object.keys(e.meta).length > 0 && (
                <div className="text-[11px] text-[#6F828A] mt-0.5 font-mono-data break-all">
                  {Object.entries(e.meta).slice(0, 3).map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : String(v).slice(0, 40)}`).join(" · ")}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Feature flags ----------
function FlagsPanel({ active }) {
  const { data, busy, err, reload } = useAdminFetch("/admin/feature-flags", active);
  const [savingId, setSavingId] = useState(null);
  if (err) return <div className="text-sm text-[#8C2A22]">Couldn&apos;t load: {err}</div>;
  if (!data) return <div className="text-xs text-[#6F828A]">Loading…</div>;
  const toggle = async (flag) => {
    setSavingId(flag.id);
    try {
      await api.put(`/admin/feature-flags/${flag.id}`, { enabled: !flag.enabled });
      toast.success(`${flag.label} ${!flag.enabled ? "enabled" : "disabled"}`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSavingId(null); }
  };
  return (
    <div className="space-y-3" data-testid="ops-flags-list">
      <ul className="divide-y divide-[#E2E8EC]">
        {data.flags.map((f) => (
          <li key={f.id} className="py-3 flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-medium text-[#2E3A3F]">{f.label}</div>
              <div className="text-[12px] text-[#6F828A]">{f.description}</div>
              {f.updated_at && (
                <div className="text-[11px] text-[#6F828A] mt-1 font-mono-data">
                  Last set by {f.updated_by} · {formatDT(f.updated_at)}
                </div>
              )}
            </div>
            <Switch
              checked={f.enabled}
              onCheckedChange={() => toggle(f)}
              disabled={savingId === f.id}
              data-testid={`ops-flag-toggle-${f.id}`}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------- Storage ----------
function StoragePanel({ active }) {
  const { data, busy, err, reload } = useAdminFetch("/admin/storage-stats?top=10", active);
  if (err) return <div className="text-sm text-[#8C2A22]">Couldn&apos;t load: {err}</div>;
  if (!data) return <div className="text-xs text-[#6F828A]">Loading…</div>;
  const fmt = (b) => {
    if (!b) return "0 B";
    const u = ["B", "KB", "MB", "GB", "TB"];
    let i = 0; let n = b; while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n < 10 ? 1 : 0)} ${u[i]}`;
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><ReloadButton onClick={reload} busy={busy} /></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-sm border border-[#E2E8EC] p-3">
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Total used</div>
          <div className="text-[18px] font-semibold tabular-nums mt-1 font-mono-data">{fmt(data.total_bytes)}</div>
        </div>
        <div className="rounded-sm border border-[#E2E8EC] p-3">
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Files</div>
          <div className="text-[18px] font-semibold tabular-nums mt-1 font-mono-data">{(data.total_files || 0).toLocaleString()}</div>
        </div>
        <div className="rounded-sm border border-[#E2E8EC] p-3">
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Court bundles</div>
          <div className="text-[18px] font-semibold tabular-nums mt-1 font-mono-data">{(data.court_bundles_generated || 0).toLocaleString()}</div>
        </div>
        <div className="rounded-sm border border-[#E2E8EC] p-3">
          <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">PDFs total</div>
          <div className="text-[18px] font-semibold tabular-nums mt-1 font-mono-data">{(data.pdfs_generated || 0).toLocaleString()}</div>
        </div>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.1em] text-[#6F828A] mb-1.5">Top households by storage</div>
        <ul className="divide-y divide-[#E2E8EC]" data-testid="ops-storage-top">
          {data.by_household.map((r) => (
            <li key={r.household_id} className="py-1.5 flex items-baseline gap-2 text-[12.5px]">
              <span className="text-[#2E3A3F] font-medium truncate">{r.name || r.household_id?.slice(0, 12)}</span>
              <span className="text-[#6F828A] font-mono-data tabular-nums ml-auto">{fmt(r.bytes)}</span>
              <span className="text-[#6F828A] font-mono-data tabular-nums">{r.count} files</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------- Household health ----------
// v77.99.75 — Hoisted out of HouseholdHealthPanel render to avoid
// destroying the subtree's DOM and state on every parent re-render.
function HouseholdHealthPill({ ok, label }) {
  const cls = ok
    ? "bg-[#E5F1EA] text-[#1E5A1E] border-[#A9D3A9]"
    : "bg-[#F0EFEE] text-[#6F828A] border-[#CFD6D8]";
  return (
    <span className={`text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border whitespace-nowrap ${cls}`}>
      {label}
    </span>
  );
}

function HouseholdHealthPanel({ active }) {
  const { data, busy, err, reload } = useAdminFetch("/admin/household-health?limit=30", active);
  if (err) return <div className="text-sm text-[#8C2A22]">Couldn&apos;t load: {err}</div>;
  if (!data) return <div className="text-xs text-[#6F828A]">Loading…</div>;
  return (
    <div className="space-y-3">
      <div className="flex justify-end"><ReloadButton onClick={reload} busy={busy} /></div>
      <ul className="divide-y divide-[#E2E8EC]" data-testid="ops-hh-health-list">
        {data.households.map((h) => (
          <li key={h.household_id} className="py-3">
            <div className="flex items-baseline gap-2 mb-1.5">
              <span className="text-[13px] font-medium text-[#2E3A3F] truncate">{h.name || h.household_id.slice(0, 12)}</span>
              <span className="text-[11px] text-[#6F828A] font-mono-data">{h.member_count} members</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <HouseholdHealthPill ok={h.verified_primary} label={h.verified_primary ? "Primary verified" : "Primary unverified"} />
              <HouseholdHealthPill ok={h.verified_co_parent} label={h.verified_co_parent ? "Co-parent verified" : "Co-parent unverified"} />
              <HouseholdHealthPill ok={h.children_added} label={h.children_added ? "Children added" : "No children"} />
              <HouseholdHealthPill ok={h.no_pending_invites} label={h.no_pending_invites ? "No pending invites" : `${h.pending_invite_count} pending invites`} />
              <HouseholdHealthPill ok={h.subscription_active_or_valid} label={h.subscription_active_or_valid ? "Subscription active" : "No active sub"} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------- Email log ----------
const RESENDABLE_TAGS = new Set([
  "password_reset", "email_verification", "subscription_confirmation",
  "coparent_invite", "mediator_invite",
]);

function EmailLogPanel({ active }) {
  const [user, setUser] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const url = `/admin/email-log?limit=50${user ? `&user_id=${encodeURIComponent(user)}` : ""}${statusFilter ? `&status=${statusFilter}` : ""}`;
  const { data, busy, err, reload } = useAdminFetch(url, active);
  if (err) return <div className="text-sm text-[#8C2A22]" data-testid="ops-panel-email-log">Couldn&apos;t load: {err}</div>;
  return (
    <div className="space-y-3" data-testid="ops-panel-email-log">
      <div className="flex flex-wrap gap-2">
        <Input placeholder="User ID filter…" value={user} onChange={(e) => setUser(e.target.value)} className="h-8 text-[12px] flex-1 min-w-[160px]" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-8 text-[12px] border border-[#D9E1E3] rounded-sm px-2">
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="bounced">Bounced</option>
          <option value="failed">Failed</option>
          <option value="suppressed">Suppressed</option>
        </select>
        <ReloadButton onClick={reload} busy={busy} />
      </div>
      {!data ? <div className="text-xs text-[#6F828A]">Loading…</div> : data.entries.length === 0 ? (
        <div className="text-sm text-[#6F828A]">No email-log entries match.</div>
      ) : (
        <ul className="divide-y divide-[#E2E8EC]" data-testid="ops-email-list">
          {data.entries.map((e, idx) => (
            <EmailLogRow
              key={`${e.ts}-${idx}`}
              entry={e}
              onResend={async () => {
                if (!RESENDABLE_TAGS.has(e.tag)) return;
                if (!window.confirm(`Resend ${e.tag} email to ${e.to}?`)) return;
                try {
                  const { data: r } = await api.post("/admin/email-log/resend", {
                    tag: e.tag, to_email: e.to, user_id: e.user_id,
                  });
                  if (r.ok) toast.success(`Resent ${e.tag} to ${e.to}`);
                  else toast.error(r.error || r.reason || `Resend failed`);
                  reload();
                } catch (err2) {
                  toast.error(err2.response?.data?.detail?.message || err2.response?.data?.detail || "Resend failed");
                }
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function EmailLogRow({ entry: e, onResend }) {
  const resendable = RESENDABLE_TAGS.has(e.tag);
  return (
    <li className="py-2 flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[12.5px]">
      <span className="font-semibold text-[#2E3A3F]">{e.tag || e.subject || "email"}</span>
      <span className="text-[#6F828A] truncate">{e.to || e.to_email}</span>
      <span className={`text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border ${e.status === "delivered" || e.status === "sent"
        ? "bg-[#E5F1EA] text-[#1E5A1E] border-[#A9D3A9]"
        : "bg-[#FBEFED] text-[#8C2A22] border-[#E8B8B2]"}`}>{e.status}</span>
      <span className="text-[10px] text-[#6F828A] font-mono-data ml-auto">{formatDT(e.ts)}</span>
      {resendable && (
        <Button
          type="button"
          variant="outline"
          onClick={onResend}
          className="h-7 px-2 text-[11px] border-[#C5B2DE] text-[#4E3877] hover:bg-[#ECE3F3] rounded-sm"
          data-testid={`ops-email-resend-${e.tag}`}
          title={`Resend the ${e.tag} email to ${e.to}.`}
        >
          <RefreshCw className="w-3 h-3 mr-1" /> Resend
        </Button>
      )}
      {e.error && <span className="text-[11px] text-[#8C2A22] basis-full mt-1 break-words">{e.error}</span>}
    </li>
  );
}

// ---------- Trial-abuse panel (v77.99.3) ----------
function TrialAbusePanel({ active }) {
  const { data, busy, err, reload } = useAdminFetch("/admin/trial-abuse?window_days=30&limit=20", active);
  if (err) return <div className="text-sm text-[#8C2A22]">Couldn&apos;t load: {err}</div>;
  if (!data) return <div className="text-xs text-[#6F828A]">Loading…</div>;
  const summary = data.summary || {};
  return (
    <div className="space-y-4" data-testid="ops-trial-abuse">
      <div className="flex justify-end"><ReloadButton onClick={reload} busy={busy} /></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Stat label="Successful trials (30d)" value={summary.successful ?? 0} tone="green" />
        <Stat label="Blocked attempts (30d)" value={summary.blocked ?? 0} tone="red" />
        <Stat label="Block rate" value={`${summary.block_rate_pct ?? 0}%`} tone="amber" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <RollupTable title="Top IPs (30d)" rows={data.by_ip || []} keyField="key" emptyMsg="No IP activity yet." />
        <RollupTable title="Top fingerprints (30d)" rows={data.by_fingerprint || []} keyField="key" emptyMsg="No fingerprint activity yet." />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.1em] text-[#6F828A] mb-2">Recent blocked attempts</div>
        {(!data.recent_blocked || data.recent_blocked.length === 0) ? (
          <div className="text-sm text-[#6F828A]">No blocked trial attempts in window.</div>
        ) : (
          <ul className="divide-y divide-[#E2E8EC]">
            {data.recent_blocked.slice(0, 15).map((r, idx) => (
              <li key={`${r.created_at}-${idx}`} className="py-2 flex flex-wrap gap-x-2 gap-y-0.5 items-baseline text-[12.5px]">
                <span className="font-semibold text-[#2E3A3F] truncate max-w-[180px]" title={r.email}>{r.email || "—"}</span>
                <span className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border bg-[#FBEFED] text-[#8C2A22] border-[#E8B8B2]">{r.blocked_reason || "blocked"}</span>
                <span className="text-[11px] text-[#6F828A] font-mono-data">ip {r.ip || "—"}</span>
                <span className="text-[11px] text-[#6F828A] font-mono-data truncate max-w-[160px]">fp {r.fingerprint_hash ? r.fingerprint_hash.slice(0, 12) + "…" : "—"}</span>
                <span className="text-[10px] text-[#6F828A] font-mono-data ml-auto">{formatDT(r.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function RollupTable({ title, rows, keyField, emptyMsg }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.1em] text-[#6F828A] mb-2">{title}</div>
      {(!rows || rows.length === 0) ? (
        <div className="text-sm text-[#6F828A]">{emptyMsg}</div>
      ) : (
        <ul className="divide-y divide-[#E2E8EC]">
          {rows.map((r, idx) => (
            <li key={`${r[keyField]}-${idx}`} className="py-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12.5px]">
              <span className="font-mono-data text-[#2E3A3F] truncate max-w-[180px]">{(r[keyField] || "—").slice(0, 24)}</span>
              <span className="text-[11px] text-[#1E5A1E]">{r.successful} ok</span>
              <span className="text-[11px] text-[#8C2A22]">{r.blocked} blocked</span>
              <span className="text-[10px] text-[#6F828A] font-mono-data ml-auto">{formatDT(r.last_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "grey" }) {
  const toneCls = tone === "green" ? "text-[#1E5A1E]" : tone === "red" ? "text-[#8C2A22]" : tone === "amber" ? "text-[#8F6B1F]" : "text-[#2E3A3F]";
  return (
    <div className="rounded-md border border-[#E2E8EC] bg-[#FBFCFC] p-3">
      <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">{label}</div>
      <div className={`text-[18px] font-semibold tabular-nums ${toneCls}`}>{value}</div>
    </div>
  );
}

// v77.99.75 — Hoisted out of RefactorScoreboardPanel and FrontendScoreboardPanel
// render functions. Both panels share the identical good/warn/default
// tone variant, so this single module-scoped component handles both
// instead of being re-created every render in two places.
function ScoreboardStat({ label, value, hint, tone = "default" }) {
  let toneCls = "border-[#E2E8EC] bg-[#F8FAFB]";
  if (tone === "good") toneCls = "border-[#B7DAC0] bg-[#E5F1EA]";
  else if (tone === "warn") toneCls = "border-[#E8D6A4] bg-[#FDF4E2]";
  return (
    <div className={`rounded-sm border p-3 ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">{label}</div>
      <div className="text-[20px] font-semibold tabular-nums mt-1 font-mono-data text-[#2E3A3F]">{value}</div>
      {hint && <div className="text-[11px] text-[#6F828A] mt-0.5">{hint}</div>}
    </div>
  );
}


// ---------- Admin broadcast message (v77.99.16) ----------
function BroadcastPanel({ active }) {
  const { data, busy, err, reload } = useAdminFetch("/admin/broadcast", active);
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [tone, setTone] = useState("info");
  const [saving, setSaving] = useState(false);

  // Hydrate the form when the GET response lands. Subsequent reloads
  // refresh the form too — handy if a second admin edits the broadcast
  // in a parallel tab.
  useEffect(() => {
    if (!data) return;
    setEnabled(!!data.enabled);
    setMessage(data.message || "");
    setTone(data.tone || "info");
  }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      const { data: out } = await api.put("/admin/broadcast", {
        enabled,
        message,
        tone,
      });
      toast.success(out.enabled ? "Broadcast enabled" : "Broadcast disabled");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save broadcast");
    } finally {
      setSaving(false);
    }
  };

  if (err) return <div className="text-sm text-[#8C2A22]">Couldn&apos;t load: {err}</div>;
  if (!data) return <div className="text-xs text-[#6F828A]">Loading…</div>;
  const charCount = (message || "").length;
  return (
    <div className="space-y-3" data-testid="ops-broadcast-panel">
      <p className="text-[12px] text-[#6F828A] leading-relaxed">
        Show a sitewide banner with a custom message to all visitors —
        signed-in or anonymous. Priority order: maintenance &gt; broadcast
        &gt; feature-outage. Disable to clear immediately; the message and
        tone are saved so you can re-enable later without retyping.
      </p>
      <div className="rounded-md border border-[#E2E8EC] bg-white p-3 space-y-3">
        <div className="flex items-start gap-3">
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            disabled={saving}
            data-testid="ops-broadcast-enabled-toggle"
          />
          <div className="text-[12.5px] text-[#2E3A3F]">
            <div className="font-medium">{enabled ? "Banner is ON" : "Banner is OFF"}</div>
            <div className="text-[11px] text-[#6F828A]">
              {enabled ? "All visitors see the message below." : "Nothing is shown."}
            </div>
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium block mb-1">
            Message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={400}
            rows={3}
            placeholder="e.g. We're investigating slow email delivery — apologies for any reset-link delays."
            disabled={saving}
            className="w-full rounded-sm border border-[#D9E1E3] bg-white px-3 py-2 text-[13px] leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#1F9FA4]"
            data-testid="ops-broadcast-message"
          />
          <div className="text-[10px] text-[#6F828A] mt-1 font-mono-data text-right">
            {charCount}/400
          </div>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium block mb-1.5">
            Tone
          </label>
          <div className="flex gap-2" data-testid="ops-broadcast-tone-group">
            {[
              { id: "info", label: "Info (teal)" },
              { id: "warning", label: "Warning (amber)" },
              { id: "urgent", label: "Urgent (red)" },
            ].map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTone(t.id)}
                disabled={saving}
                className={
                  "text-[11.5px] px-3 py-1.5 rounded-sm border " +
                  (tone === t.id
                    ? "bg-[#116B6E] text-white border-[#116B6E]"
                    : "bg-white text-[#48595E] border-[#D9E1E3] hover:bg-[#FAFCFC]")
                }
                data-testid={`ops-broadcast-tone-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {(message || "").trim() && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-medium mb-1.5">
              Preview
            </div>
            <BroadcastPreview message={message} tone={tone} />
          </div>
        )}

        <div className="flex justify-between items-center pt-1">
          <div className="text-[10.5px] text-[#6F828A]">
            {data.updated_at && (
              <>Last set by {data.updated_by_email} · {formatDT(data.updated_at)}</>
            )}
          </div>
          <Button
            type="button"
            onClick={save}
            disabled={saving || busy}
            className="bg-[#116B6E] hover:bg-[#0F5658] text-white text-[12.5px] h-8 px-4 rounded-sm"
            data-testid="ops-broadcast-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function BroadcastPreview({ message, tone }) {
  const palettes = {
    info: "bg-[#E8F5F5] text-[#0F5658] border-[#A7D8DA]",
    warning: "bg-[#FDF4E2] text-[#8F6B1F] border-[#E6CFA2]",
    urgent: "bg-[#FBEFED] text-[#8C2A22] border-[#E8B8B2]",
  };
  return (
    <div
      className={`border rounded-sm px-3 py-2 text-[12.5px] flex items-center gap-2 ${palettes[tone] || palettes.info}`}
      data-testid="ops-broadcast-preview"
    >
      <AlertTriangle className="w-4 h-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}


// ---------- Test email patterns (v77.99.16) ----------
function TestPatternsPanel({ active }) {
  const { data, busy, err, reload } = useAdminFetch("/admin/test-email-patterns", active);
  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newRegex, setNewRegex] = useState("");
  const [adding, setAdding] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);

  const add = async (e) => {
    e.preventDefault();
    if (!newKey.trim() || !newLabel.trim() || !newRegex.trim()) {
      toast.error("Fill in key, label and regex.");
      return;
    }
    setAdding(true);
    try {
      await api.post("/admin/test-email-patterns", {
        key: newKey.trim().toLowerCase(),
        label: newLabel.trim(),
        regex: newRegex.trim(),
      });
      toast.success(`Pattern '${newKey}' added`);
      setNewKey("");
      setNewLabel("");
      setNewRegex("");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add pattern");
    } finally {
      setAdding(false);
    }
  };

  const remove = async (key, label) => {
    if (!window.confirm(`Remove the custom pattern "${label}"?\n\nThis only removes the rule — no users are deleted.`)) return;
    setDeletingKey(key);
    try {
      await api.delete(`/admin/test-email-patterns/${key}`);
      toast.success(`Pattern '${label}' removed`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove pattern");
    } finally {
      setDeletingKey(null);
    }
  };

  if (err) return <div className="text-sm text-[#8C2A22]">Couldn&apos;t load: {err}</div>;
  if (!data) return <div className="text-xs text-[#6F828A]">Loading…</div>;

  return (
    <div className="space-y-3" data-testid="ops-test-patterns-panel">
      <p className="text-[12px] text-[#6F828A] leading-relaxed">
        These regex patterns flag accounts that look like test / development
        users in the data-audit screen and feed the bulk cleanup tool. The
        five built-in patterns ship with the codebase and can&apos;t be
        removed. Add your own (e.g. <code className="font-mono-data text-[11px]">@xample.com</code>) when test
        accounts leak in from a new source.
      </p>

      <form
        onSubmit={add}
        className="rounded-md border border-[#E2E8EC] bg-[#FBFCFC] p-3 space-y-2"
        data-testid="ops-test-patterns-add-form"
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            placeholder="key (e.g. xample_com)"
            maxLength={40}
            disabled={adding}
            className="text-[12.5px] h-9"
            data-testid="ops-test-patterns-input-key"
          />
          <Input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="label (e.g. @xample.com)"
            maxLength={80}
            disabled={adding}
            className="text-[12.5px] h-9"
            data-testid="ops-test-patterns-input-label"
          />
          <Input
            value={newRegex}
            onChange={(e) => setNewRegex(e.target.value)}
            placeholder={"regex (e.g. @xample\\.com$)"}
            maxLength={200}
            disabled={adding}
            className="text-[12.5px] h-9 font-mono-data"
            data-testid="ops-test-patterns-input-regex"
          />
        </div>
        <div className="flex justify-between items-center pt-1">
          <div className="text-[10.5px] text-[#6F828A]">
            Regex is applied case-insensitive against the user&apos;s email field.
          </div>
          <Button
            type="submit"
            disabled={adding}
            className="bg-[#116B6E] hover:bg-[#0F5658] text-white text-[12px] h-8 px-3 rounded-sm"
            data-testid="ops-test-patterns-add-button"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            {adding ? "Adding…" : "Add pattern"}
          </Button>
        </div>
      </form>

      <ul className="divide-y divide-[#E2E8EC] rounded-md border border-[#E2E8EC] bg-white">
        {data.patterns.map((p) => (
          <li
            key={p.key}
            className="py-2.5 px-3 flex items-center gap-3"
            data-testid={`ops-test-patterns-row-${p.key}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] font-medium text-[#2E3A3F] flex items-center gap-2">
                {p.label}
                {p.builtin ? (
                  <span className="text-[9px] uppercase tracking-[0.1em] bg-[#E2E8EC] text-[#48595E] rounded px-1.5 py-0.5">Built-in</span>
                ) : (
                  <span className="text-[9px] uppercase tracking-[0.1em] bg-[#E8F5F5] text-[#0F5658] rounded px-1.5 py-0.5">Custom</span>
                )}
              </div>
              <div className="text-[10.5px] text-[#6F828A] font-mono-data truncate">
                {p.key} · /{p.regex}/i
              </div>
              {!p.builtin && p.created_by_email && (
                <div className="text-[10px] text-[#6F828A] mt-0.5">
                  Added by {p.created_by_email}{p.created_at ? ` · ${formatDT(p.created_at)}` : ""}
                </div>
              )}
            </div>
            {!p.builtin && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => remove(p.key, p.label)}
                disabled={deletingKey === p.key}
                className="h-8 px-2 text-[#8C2A22] hover:bg-[#FBEFED]"
                data-testid={`ops-test-patterns-remove-${p.key}`}
                title="Remove pattern"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}



// ---------- Refactor scoreboard (v77.99.19) ----------
function RefactorScoreboardPanel({ active }) {
  const { data, busy, err, reload } = useAdminFetch("/admin/refactor-scoreboard", active);
  if (err) return <div className="text-sm text-[#8C2A22]">Couldn&apos;t load: {err}</div>;
  if (!data) return <div className="text-xs text-[#6F828A]">Loading…</div>;
  const t = data.totals || {};
  const ratioPct = ((t.monolith_ratio || 0) * 100).toFixed(1);
  const ratioHealthy = (t.monolith_ratio || 0) < 0.05;

  // Largest module — flag it as a future-refactor target.
  const largest = [...(data.modules || [])].sort((a, b) => b.lines - a.lines)[0];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-[#6F828A] max-w-2xl">
          Live snapshot of the backend modularisation state. Drives the v77.99.19
          Phase&nbsp;2 hardening goal: <span className="font-medium text-[#2E3A3F]">zero handlers in <code>server.py</code></span>.
        </p>
        <ReloadButton onClick={reload} busy={busy} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ops-refactor-stats">
        <ScoreboardStat
          label="server.py"
          value={data.server?.lines?.toLocaleString() ?? "—"}
          hint={`${data.server?.handlers ?? 0} inline handler${data.server?.handlers === 1 ? "" : "s"}`}
          tone={data.server?.handlers === 0 ? "good" : "warn"}
        />
        <ScoreboardStat
          label="Route modules"
          value={t.modules ?? 0}
          hint={`${t.route_handlers ?? 0} handlers extracted`}
        />
        <ScoreboardStat
          label="Monolith ratio"
          value={`${ratioPct}%`}
          hint={ratioHealthy ? "Healthy · target < 5%" : "Drift! Re-extract"}
          tone={ratioHealthy ? "good" : "warn"}
        />
        <ScoreboardStat
          label="Total handlers"
          value={t.total_handlers ?? 0}
          hint="Across server + routes"
        />
      </div>

      {largest && (
        <div className="text-[11.5px] text-[#6F828A]">
          Largest route module: <span className="font-medium text-[#2E3A3F] font-mono-data">{largest.name}</span> · {largest.lines.toLocaleString()} lines · {largest.handlers} handler{largest.handlers === 1 ? "" : "s"}.
        </div>
      )}

      <div>
        <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium mb-2">
          Module breakdown
        </div>
        <div className="rounded-sm border border-[#E2E8EC] overflow-hidden" data-testid="ops-refactor-modules-table">
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#F8FAFB] text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Module</th>
                <th className="text-right px-3 py-2 font-medium">Lines</th>
                <th className="text-right px-3 py-2 font-medium">Handlers</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8EC]">
              {(data.modules || []).map((m) => (
                <tr
                  key={m.name}
                  className="hover:bg-[#F8FAFB]"
                  data-testid={`ops-refactor-module-${m.name.replace(/\.py$/, "")}`}
                >
                  <td className="px-3 py-2 font-mono-data text-[#2E3A3F]">{m.name}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular-nums text-[#2E3A3F]">{m.lines.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular-nums text-[#2E3A3F]">{m.handlers}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#F8FAFB] text-[#2E3A3F]">
              <tr>
                <td className="px-3 py-2 font-medium">Total</td>
                <td className="px-3 py-2 text-right font-mono-data tabular-nums font-medium">
                  {(data.modules || []).reduce((sum, m) => sum + (m.lines || 0), 0).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right font-mono-data tabular-nums font-medium">{t.route_handlers ?? 0}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-[#6F828A]">
        Snapshot generated: <span className="font-mono-data">{formatDT(data.generated_at)}</span>
      </p>
    </div>
  );
}

// ---------- Frontend health scoreboard (v77.99.20) ----------
function FrontendScoreboardPanel({ active }) {
  const { data, busy, err, reload } = useAdminFetch("/admin/frontend-scoreboard", active);
  if (err) return <div className="text-sm text-[#8C2A22]">Couldn&apos;t load: {err}</div>;
  if (!data) return <div className="text-xs text-[#6F828A]">Loading…</div>;
  const t = data.totals || {};
  const ratioPct = ((t.page_monolith_ratio || 0) * 100).toFixed(1);
  const ratioHealthy = (t.page_monolith_ratio || 0) < 0.25;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-[#6F828A] max-w-2xl">
          Sibling to the backend Refactor scoreboard. Flags pages that
          have grown back into monoliths so the next decomposition pass
          has a clear target.
        </p>
        <ReloadButton onClick={reload} busy={busy} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ops-frontend-stats">
        <ScoreboardStat
          label="Pages tracked"
          value={t.pages_count ?? 0}
          hint={`${(t.pages_lines || 0).toLocaleString()} total lines`}
        />
        <ScoreboardStat
          label="Biggest page"
          value={(t.biggest_page_lines || 0).toLocaleString()}
          hint={t.biggest_page || "—"}
          tone={t.biggest_page_lines > 2500 ? "warn" : "default"}
        />
        <ScoreboardStat
          label="Page monolith ratio"
          value={`${ratioPct}%`}
          hint={ratioHealthy ? "Healthy · target < 25%" : "One page hoarding logic"}
          tone={ratioHealthy ? "good" : "warn"}
        />
        <ScoreboardStat
          label="Component dirs"
          value={(data.dirs || []).length}
          hint="Domain-scoped folders"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium mb-2">
            Top 10 pages by size
          </div>
          <div className="rounded-sm border border-[#E2E8EC] overflow-hidden" data-testid="ops-frontend-pages-table">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F8FAFB] text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Page</th>
                  <th className="text-right px-3 py-2 font-medium">Lines</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8EC]">
                {(data.pages || []).slice(0, 10).map((p) => (
                  <tr key={p.name} className={`hover:bg-[#F8FAFB] ${p.lines > 2500 ? "bg-[#FDF4E2]" : ""}`}>
                    <td className="px-3 py-2 font-mono-data text-[#2E3A3F] truncate max-w-[260px]" title={p.name}>{p.name}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular-nums text-[#2E3A3F]">{p.lines.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium mb-2">
            Component directories
          </div>
          <div className="rounded-sm border border-[#E2E8EC] overflow-hidden" data-testid="ops-frontend-dirs-table">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F8FAFB] text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Directory</th>
                  <th className="text-right px-3 py-2 font-medium">Files</th>
                  <th className="text-right px-3 py-2 font-medium">Lines</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8EC]">
                {(data.dirs || []).map((d) => (
                  <tr key={d.name} className="hover:bg-[#F8FAFB]">
                    <td className="px-3 py-2 font-mono-data text-[#2E3A3F]">{d.name}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular-nums text-[#2E3A3F]">{d.files}</td>
                    <td className="px-3 py-2 text-right font-mono-data tabular-nums text-[#2E3A3F]">{d.lines.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium mb-2">
          Top 10 largest frontend files
        </div>
        <div className="rounded-sm border border-[#E2E8EC] overflow-hidden" data-testid="ops-frontend-largest-table">
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#F8FAFB] text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Path</th>
                <th className="text-right px-3 py-2 font-medium">Lines</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8EC]">
              {(data.largest || []).map((f) => (
                <tr key={f.name} className="hover:bg-[#F8FAFB]">
                  <td className="px-3 py-2 font-mono-data text-[#2E3A3F] truncate max-w-[500px]" title={f.name}>{f.name}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular-nums text-[#2E3A3F]">{f.lines.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-[#6F828A]">
        Snapshot generated: <span className="font-mono-data">{formatDT(data.generated_at)}</span>
      </p>
    </div>
  );
}



// ---------- Pending-invite promotion (v77.99.20) ----------
function PendingInvitesPanel({ active }) {
  const { data, busy, err, reload } = useAdminFetch("/admin/pending-invites", active);
  const [promoting, setPromoting] = useState({});
  const [results, setResults] = useState({});
  const [confirmingId, setConfirmingId] = useState(null);

  const promote = async (row) => {
    setPromoting((p) => ({ ...p, [row.id]: true }));
    try {
      const { data: res } = await api.post(`/admin/households/${row.household_id}/invites/${row.id}/promote`);
      setResults((r) => ({ ...r, [row.id]: { ok: true, ...res } }));
      toast.success(`Promoted ${row.email} to placeholder co-parent`);
      // Trigger reload so the row disappears from the pending list
      setTimeout(() => reload(), 500);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Promotion failed";
      setResults((r) => ({ ...r, [row.id]: { ok: false, error: String(detail) } }));
      toast.error(`Promotion failed: ${detail}`);
    } finally {
      setPromoting((p) => {
        const next = { ...p };
        delete next[row.id];
        return next;
      });
      setConfirmingId(null);
    }
  };

  if (err) return <div className="text-sm text-[#8C2A22]">Couldn&apos;t load: {err}</div>;
  if (!data) return <div className="text-xs text-[#6F828A]">Loading…</div>;
  const rows = data.invites || [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[12px] text-[#6F828A] max-w-2xl">
          Promote a stuck pending invite to a placeholder co-parent so the primary parent can immediately log expenses, events, and messages attributed to them. When the real co-parent eventually signs up via a new invite, their data is merged onto the new account automatically.
        </p>
        <ReloadButton onClick={reload} busy={busy} />
      </div>

      <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
        {data.count} pending invite{data.count === 1 ? "" : "s"}
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-[#6F828A] italic" data-testid="ops-pending-invites-empty">
          No pending invites across the platform — every issued invite has been accepted or revoked.
        </div>
      ) : (
        <div className="rounded-sm border border-[#E2E8EC] overflow-hidden" data-testid="ops-pending-invites-table">
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#F8FAFB] text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Email</th>
                <th className="text-left px-3 py-2 font-medium">Household</th>
                <th className="text-left px-3 py-2 font-medium">Children</th>
                <th className="text-left px-3 py-2 font-medium">Created</th>
                <th className="text-right px-3 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8EC]">
              {rows.map((row) => {
                const result = results[row.id];
                const isBusy = !!promoting[row.id];
                const isConfirming = confirmingId === row.id;
                return (
                  <tr
                    key={row.id}
                    className="hover:bg-[#F8FAFB]"
                    data-testid={`ops-pending-invite-row-${row.id}`}
                  >
                    <td className="px-3 py-2 font-mono-data text-[#2E3A3F]">{row.email}</td>
                    <td className="px-3 py-2 text-[#2E3A3F]">{row.household_name || row.household_id?.slice(0, 8)}</td>
                    <td className="px-3 py-2 text-[#2E3A3F]">{(row.child_names || []).join(", ")}</td>
                    <td className="px-3 py-2 font-mono-data text-[#6F828A]">{formatDT(row.created_at)}</td>
                    <td className="px-3 py-2 text-right">
                      {result?.ok ? (
                        <span className="inline-flex items-center gap-1 text-[#116B6E] text-[11px]">
                          ✓ Promoted
                        </span>
                      ) : isConfirming ? (
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => promote(row)}
                            disabled={isBusy}
                            className="text-[11px] px-2 py-1 bg-[#7A5AA6] text-white rounded-sm hover:bg-[#634785]"
                            data-testid={`ops-pending-invite-confirm-${row.id}`}
                          >
                            {isBusy ? "Promoting…" : "Confirm"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmingId(null)}
                            className="text-[11px] px-2 py-1 border border-[#D9E1E3] rounded-sm hover:bg-[#F8FAFB]"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmingId(row.id)}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 border border-[#7A5AA6] text-[#4E3877] rounded-sm hover:bg-[#ECE3F3]"
                          data-testid={`ops-pending-invite-promote-${row.id}`}
                        >
                          <UserPlus className="w-3 h-3" /> Promote to placeholder
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- Release notes admin (v77.99.21) ----------
const EMPTY_DRAFT = {
  version: "",
  date: "",
  title: "",
  body: "",
  tone: "feature",
  published: true,
  // v77.99.22 — Customer / internal split + polished SaaS fields.
  audience: "customer",
  category: "",
  description: "",
  highlight: false,
  learn_more_url: "",
  screenshot_url: "",
};

// Pre-baked category options matching the parent-facing badge palette
// in WhatsNewModal.jsx. The form still allows free-text via the
// "Custom…" option for one-off categories ("Notifications", etc.).
const CATEGORY_OPTIONS = [
  "",                // (no category)
  "Expenses",
  "Calendar",
  "Messages",
  "Requests",
  "Journal",
  "Documents",
  "Children",
  "Privacy",
  "Mobile",
  "Accessibility",
  "Performance",
  "Exports",
  "Notifications",
];

function ReleaseNotesPanel({ active }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  // v77.99.22 — Audience filter for the list view. "all" keeps the
  // legacy behaviour; "customer" / "internal" focus the stream.
  const [audienceFilter, setAudienceFilter] = useState("all");
  const [classifying, setClassifying] = useState(false);

  const reload = async () => {
    if (!active) return;
    setBusy(true);
    setErr(null);
    try {
      const params = audienceFilter !== "all" ? { audience: audienceFilter } : {};
      const { data: res } = await api.get("/admin/release-notes", { params });
      setData(res);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Couldn't load release notes");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (active) reload();
  }, [active, audienceFilter]);

  const resetDraft = () => {
    setDraft(EMPTY_DRAFT);
    setEditingId(null);
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setDraft({
      version: note.version || "",
      date: note.date || "",
      title: note.title || "",
      body: Array.isArray(note.body) ? note.body.join("\n") : (note.body || ""),
      tone: note.tone || "feature",
      published: !!note.published,
      audience: note.audience || "customer",
      category: note.category || "",
      description: note.description || "",
      highlight: !!note.highlight,
      learn_more_url: note.learn_more_url || "",
      screenshot_url: note.screenshot_url || "",
    });
  };

  const submit = async () => {
    if (!draft.version || !draft.date || !draft.title || !draft.body) {
      toast.error("Version, date, title and body are required");
      return;
    }
    const payload = {
      ...draft,
      // Normalise body: split into bullets on newline if multi-line.
      body: draft.body.includes("\n")
        ? draft.body.split(/\n+/).map((s) => s.trim()).filter(Boolean)
        : draft.body,
      // Empty strings → null for the optional metadata fields so the
      // backend stores `None` rather than "".
      category: draft.category.trim() || null,
      description: draft.description.trim() || null,
      learn_more_url: draft.learn_more_url.trim() || null,
      screenshot_url: draft.screenshot_url.trim() || null,
    };
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/release-notes/${editingId}`, payload);
        toast.success("Release note updated");
      } else {
        await api.post("/admin/release-notes", payload);
        toast.success(
          payload.audience === "internal"
            ? "Internal note published (hidden from users)"
            : "Release note published",
        );
      }
      resetDraft();
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // v77.99.22 — One-click triage for legacy rows that pre-date the
  // audience field. Marks every "unclassified" row as internal so the
  // parent-facing modal stops surfacing old refactor/admin notes.
  const bulkHideLegacy = async () => {
    if (!window.confirm(
      "Mark every legacy (unclassified) release note as INTERNAL?\n\n"
      + "This will hide them from the parent-facing 'What's new' modal. "
      + "You can still re-classify individual notes back to 'customer' "
      + "afterwards.",
    )) return;
    setClassifying(true);
    try {
      const { data: res } = await api.post(
        "/admin/release-notes/bulk-classify",
        { all_unclassified: true, audience: "internal" },
      );
      toast.success(`Hidden ${res.modified} legacy notes from parents`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Bulk classify failed");
    } finally {
      setClassifying(false);
    }
  };

  const remove = async (note) => {
    if (!window.confirm(`Delete release note ${note.version}?`)) return;
    try {
      await api.delete(`/admin/release-notes/${note.id}`);
      toast.success("Release note deleted");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Delete failed");
    }
  };

  if (err) return <div className="text-sm text-[#8C2A22]">Couldn&apos;t load: {err}</div>;
  if (!data) return <div className="text-xs text-[#6F828A]">Loading…</div>;
  const notes = data.notes || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[12px] text-[#6F828A] max-w-2xl">
          Compose, edit and publish entries for the in-app &quot;What&apos;s new&quot; modal.
          {" "}
          <b>Audience</b> controls who sees the note: <i>Customer</i> appears
          in the parent-facing modal; <i>Internal</i> stays here for ops /
          engineering reference only.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={bulkHideLegacy}
            disabled={classifying}
            className="text-[11px] px-3 py-1.5 border border-[#D9E1E3] rounded-sm hover:bg-[#F8FAFB] disabled:opacity-50"
            data-testid="ops-release-bulk-hide-legacy"
            title="Mark every release note missing an audience as Internal."
          >
            {classifying ? "Working…" : "Hide legacy notes from parents"}
          </button>
          <ReloadButton onClick={reload} busy={busy} />
        </div>
      </div>

      {/* Audience filter tabs */}
      <div className="flex items-center gap-1 text-[11px]" data-testid="ops-release-audience-tabs">
        {[
          { id: "all", label: "All", count: data?.count ?? 0 },
          { id: "customer", label: "Customer-facing", count: data?.counts?.customer ?? 0 },
          { id: "internal", label: "Internal", count: data?.counts?.internal ?? 0 },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setAudienceFilter(t.id)}
            className={
              "px-3 py-1.5 rounded-sm border transition-colors "
              + (audienceFilter === t.id
                ? "border-[#1F9FA4] bg-[#E7F4F3] text-[#0F6B6E] font-medium"
                : "border-[#D9E1E3] bg-white text-[#46545F] hover:bg-[#F8FAFB]")
            }
            data-testid={`ops-release-audience-tab-${t.id}`}
          >
            {t.label}
            {audienceFilter === t.id && data?.counts && (
              <span className="ml-1.5 text-[10px] opacity-75">·&nbsp;{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Compose / edit form */}
      <div className="border border-[#E2E8EC] bg-[#F8FAFB] rounded-sm p-4 space-y-3" data-testid="ops-release-form">
        <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
          {editingId ? `Editing ${draft.version}` : "Compose new release note"}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Version</label>
            <input
              type="text"
              value={draft.version}
              onChange={(e) => setDraft({ ...draft, version: e.target.value })}
              placeholder="v77.99.22"
              className="w-full px-2 py-1.5 border border-[#D9E1E3] rounded-sm text-sm font-mono-data"
              data-testid="ops-release-version-input"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Date (YYYY-MM-DD)</label>
            <input
              type="date"
              value={draft.date}
              onChange={(e) => setDraft({ ...draft, date: e.target.value })}
              className="w-full px-2 py-1.5 border border-[#D9E1E3] rounded-sm text-sm font-mono-data"
              data-testid="ops-release-date-input"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Tone</label>
            <select
              value={draft.tone}
              onChange={(e) => setDraft({ ...draft, tone: e.target.value })}
              className="w-full px-2 py-1.5 border border-[#D9E1E3] rounded-sm text-sm bg-white"
              data-testid="ops-release-tone-select"
            >
              <option value="feature">Feature (teal)</option>
              <option value="fix">Fix (amber)</option>
              <option value="ops">Ops (violet)</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Title</label>
          <input
            type="text"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder={draft.audience === "customer"
              ? "You can now export yearly expense snapshots."
              : "Internal: refactored expenses module"}
            className="w-full px-2 py-1.5 border border-[#D9E1E3] rounded-sm text-sm"
            data-testid="ops-release-title-input"
          />
        </div>

        {/* v77.99.22 — Audience radio + customer-only polish fields. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">
              Audience
            </label>
            <div className="flex items-center gap-2" data-testid="ops-release-audience-input">
              {[
                { id: "customer", label: "Customer-facing", hint: "Shows in parent modal" },
                { id: "internal", label: "Internal", hint: "Ops only" },
              ].map((opt) => (
                <label
                  key={opt.id}
                  className={
                    "flex-1 cursor-pointer px-2.5 py-1.5 border rounded-sm text-[12px] "
                    + (draft.audience === opt.id
                      ? "border-[#1F9FA4] bg-[#E7F4F3] text-[#0F6B6E]"
                      : "border-[#D9E1E3] bg-white text-[#46545F]")
                  }
                  data-testid={`ops-release-audience-${opt.id}`}
                >
                  <input
                    type="radio"
                    name="audience"
                    value={opt.id}
                    checked={draft.audience === opt.id}
                    onChange={(e) => setDraft({ ...draft, audience: e.target.value })}
                    className="mr-1.5"
                  />
                  <span className="font-medium">{opt.label}</span>
                  <span className="block text-[10px] text-[#6F828A] mt-0.5">{opt.hint}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">
              Category badge {draft.audience === "internal" && <span className="opacity-60">(hidden — internal)</span>}
            </label>
            <select
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              className="w-full px-2 py-1.5 border border-[#D9E1E3] rounded-sm text-sm bg-white"
              disabled={draft.audience === "internal"}
              data-testid="ops-release-category-select"
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c || "none"} value={c}>{c || "— none —"}</option>
              ))}
            </select>
          </div>
        </div>

        {draft.audience === "customer" && (
          <>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">
                Short description (optional, plain English, max 280 chars)
              </label>
              <textarea
                rows={2}
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                maxLength={280}
                placeholder="One calm sentence summarising the user benefit."
                className="w-full px-2 py-1.5 border border-[#D9E1E3] rounded-sm text-sm"
                data-testid="ops-release-description-input"
              />
              <div className="text-[10px] uppercase tracking-[0.08em] text-[#6F828A] text-right mt-0.5">
                {draft.description.length}/280
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">
                  &ldquo;Learn more&rdquo; URL (optional)
                </label>
                <input
                  type="url"
                  value={draft.learn_more_url}
                  onChange={(e) => setDraft({ ...draft, learn_more_url: e.target.value })}
                  placeholder="https://help.coparentconsole.com/…"
                  className="w-full px-2 py-1.5 border border-[#D9E1E3] rounded-sm text-sm"
                  data-testid="ops-release-learn-more-input"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">
                  Screenshot URL (optional)
                </label>
                <input
                  type="url"
                  value={draft.screenshot_url}
                  onChange={(e) => setDraft({ ...draft, screenshot_url: e.target.value })}
                  placeholder="https://…/screenshot.png"
                  className="w-full px-2 py-1.5 border border-[#D9E1E3] rounded-sm text-sm"
                  data-testid="ops-release-screenshot-input"
                />
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-[12px] text-[#2E3A3F]">
              <input
                type="checkbox"
                checked={draft.highlight}
                onChange={(e) => setDraft({ ...draft, highlight: e.target.checked })}
                data-testid="ops-release-highlight-input"
              />
              Highlight with a &ldquo;New&rdquo; pill
            </label>
          </>
        )}
        <div>
          <label className="block text-[10px] uppercase tracking-[0.1em] text-[#6F828A] mb-1">Body</label>
          <textarea
            rows={5}
            value={draft.body}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            placeholder="One line per bullet point (or one paragraph for plain text)"
            className="w-full px-2 py-1.5 border border-[#D9E1E3] rounded-sm text-sm"
            data-testid="ops-release-body-input"
          />
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="inline-flex items-center gap-2 text-[12px] text-[#2E3A3F]">
            <input
              type="checkbox"
              checked={draft.published}
              onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
              data-testid="ops-release-published-input"
            />
            Published (visible to users)
          </label>
          <div className="flex items-center gap-2">
            {editingId && (
              <button
                type="button"
                onClick={resetDraft}
                className="text-[11px] px-3 py-1.5 border border-[#D9E1E3] rounded-sm hover:bg-[#F8FAFB]"
                data-testid="ops-release-cancel"
              >
                Cancel edit
              </button>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="inline-flex items-center gap-1 text-[12px] px-3 py-1.5 bg-[#7A5AA6] text-white rounded-sm hover:bg-[#634785] disabled:opacity-50"
              data-testid="ops-release-save"
            >
              {saving ? "Saving…" : (editingId ? "Save changes" : "Publish")}
            </button>
          </div>
        </div>
      </div>

      {/* List of existing notes */}
      <div className="text-[11px] uppercase tracking-[0.12em] text-[#6F828A] font-medium">
        {notes.length} note{notes.length === 1 ? "" : "s"}
      </div>
      {notes.length === 0 ? (
        <div className="text-sm text-[#6F828A] italic" data-testid="ops-release-empty">
          No release notes yet. Compose one above.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="ops-release-list">
          {notes.map((n) => (
            <li
              key={n.id}
              className={`border ${n.published ? "border-[#E2E8EC]" : "border-[#E8D6A4] bg-[#FDF4E2]"} rounded-sm p-3`}
              data-testid={`ops-release-row-${n.id}`}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono-data text-[11px] text-[#6F828A]">{n.version}</span>
                  <span className="font-mono-data text-[11px] text-[#6F828A]">· {n.date}</span>
                  <span className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border border-[#D9E1E3] bg-white rounded-sm text-[#6F828A]">
                    {n.tone}
                  </span>
                  {/* v77.99.22 — Audience badge so ops can see at a glance which stream this row belongs to. */}
                  <span
                    className={
                      "text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border rounded-sm "
                      + (n.audience === "customer"
                        ? "border-[#A7D8DA] bg-[#E7F4F3] text-[#0F6B6E]"
                        : "border-[#D9E1E3] bg-[#F4F6F7] text-[#46545F]")
                    }
                    data-testid={`ops-release-audience-${n.id}`}
                  >
                    {n.audience === "customer" ? "Customer" : "Internal"}
                  </span>
                  {n.category && (
                    <span className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border border-[#D9E1E3] bg-white rounded-sm text-[#46545F]">
                      {n.category}
                    </span>
                  )}
                  {n.highlight && (
                    <span className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm bg-[#1F9FA4] text-white">
                      New
                    </span>
                  )}
                  {!n.published && (
                    <span className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border border-[#E8D6A4] bg-[#FDF4E2] rounded-sm text-[#8F6B1F]">
                      Draft
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => startEdit(n)}
                    className="text-[11px] px-2 py-1 border border-[#D9E1E3] rounded-sm hover:bg-[#F8FAFB]"
                    data-testid={`ops-release-edit-${n.id}`}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(n)}
                    className="text-[11px] px-2 py-1 border border-[#E8B8B2] text-[#8C2A22] rounded-sm hover:bg-[#FBEFED]"
                    data-testid={`ops-release-delete-${n.id}`}
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="font-medium text-[#2E3A3F] mt-1.5">{n.title}</div>
              <div className="text-[12.5px] text-[#6F828A] mt-1">
                {Array.isArray(n.body) ? (
                  <ul className="list-disc pl-5 space-y-0.5">
                    {n.body.map((line, i) => <li key={`${n.id}-bullet-${i}`}>{line}</li>)}
                  </ul>
                ) : (
                  <p>{n.body}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

